import styled from 'styled-components';
import { useContext, useState, useMemo } from 'react';
import { EditorContext } from '../../utils/contexts';
import { Section, Container, Icon } from './ThemeSelector/components';
import Theme from './ThemeSelector/Theme';
import { availableThemes } from './ThemeSelector/updateTheme';
import { Autocomplete, Chip, TextField } from '@mui/material';

function ThemeSelector({
  className,
  showThemeSelector,
  setCanOverrideTheme,
  setUpdateThemeContents,
}) {
  const { settings, siteSettings, updateTheme, updateSettings } = useContext(EditorContext);
  const isGamification =
    settings?.element_subtype === 'gamification' || settings?.subtype === 'gamification';
  const isForms =
    settings?.element_subtype === 'forms' || settings?.subtype === 'forms';
  const gameType = settings?.gamification?.game_type || 'spin_wheel';

  const formTemplates = isForms
    ? (siteSettings?.available_form_templates || []).filter(
        (t) => !settings.form_type || t.form_type === settings.form_type || t.tags?.includes('forms')
      ).map((t) => ({ tags: ['forms'], ...t }))
    : [];

  const themes = isForms
    ? formTemplates
    : availableThemes(siteSettings, settings.type)
        .filter(({ id }) => id !== 'autodetect')
        .filter((theme) => {
          if (isGamification) return (theme.tags || []).includes('gamification');
          return !(theme.tags || []).includes('gamification') && !(theme.tags || []).includes('forms');
        })
        .filter((theme) => {
          if (!isGamification) return true;
          if (gameType === 'scratch_card') return theme.id.startsWith('gamification-scratch');
          if (gameType === 'surprise_box') return theme.id.startsWith('gamification-loot');
          return theme.id.startsWith('gamification-spin');
        });
  const tagOptions = [
    ...new Set(
      themes
        .map((theme) => {
          return theme.tags;
        })
        .flat()
    ),
  ];
  const [tag, setTag] = useState([]);

  const displayThemes = useMemo(
    () =>
      tag.length === 0
        ? themes
        : themes.filter((theme) =>
            tag.some((tag) => theme.tags.includes(tag.toLowerCase()))
          ),
    [settings.type, settings.form_type, gameType, tag]
  );

  const chooseTheme = (theme) => {
    if (isForms) {
      updateSettings({
        form_template_id: theme.id,
        form_template_name: theme.name || theme.id,
        form_template_theme: theme.form_template_theme,
        ...(theme.defaults?.questions ? { form_questions: theme.defaults.questions } : {}),
      });
    } else {
      updateTheme(theme.id);
      siteSettings.site_theme && setCanOverrideTheme(true);
      setUpdateThemeContents(true);
    }
    showThemeSelector(false);
  };

  return (
    <div className={className}>
      <Container>
        <Icon onClick={() => showThemeSelector(false)}>
          <i className="icon-close" />
        </Icon>

        {/* <Section>
          <h3>Color autodetection <span>Colors grabbed from the site</span></h3>
        </Section>

        <Section>
          <AutodetectButton onClick={() => chooseTheme({id: 'autodetect'})}>
            <img alt="Autodetection" src={AutodetectionImg}/>
            <span>Autodetect colors from site</span>
          </AutodetectButton>
        </Section> */}

        <Section>
          <h3>
            {isForms
              ? 'Forms & Surveys Templates'
              : isGamification
                ? gameType === 'scratch_card'
                  ? 'Scratch Card Templates'
                  : gameType === 'surprise_box'
                    ? 'Loot Box Templates'
                    : 'Spin & Win Templates'
                : `${settings.type} Templates`}
          </h3>
        </Section>

        <Section>
          {!isGamification && !isForms && (
            <Autocomplete
              multiple
              options={tagOptions || []}
              getOptionLabel={(option) => option.toUpperCase()}
              filterSelectedOptions
              value={tag}
              renderTags={(value, getThemeProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getThemeProps({ index })}
                    variant="outlined"
                    label={option.toUpperCase()}
                    sx={{
                      fontSize: '12px',
                      backgroundColor: 'antiquewhite',
                    }}
                  />
                ))
              }
              onChange={(_event, newValue) => {
                setTag(newValue);
              }}
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Filter templates"
                    placeholder="Select a category"
                    margin="normal"
                    fullWidth
                  />
                );
              }}
            />
          )}
        </Section>

        <Section>
          {displayThemes.length > 0 ? (
            displayThemes.map((theme) => (
              <Theme
                key={theme.id}
                type={settings.type}
                theme={theme}
                onClick={chooseTheme}
              />
            ))
          ) : (
            <h6>
              {isGamification
                ? gameType === 'scratch_card'
                  ? 'No scratch card themes found.'
                  : gameType === 'surprise_box'
                    ? 'No loot box themes found.'
                    : 'No spin themes found.'
                : 'No Theme Found'}
            </h6>
          )}
        </Section>
      </Container>
    </div>
  );
}

export default styled(ThemeSelector)`
  height: 100vh;
  background: #f1f0f0;
  position: relative;
`;
